<template>
  <div class="sync-management-report">
    <div class="sync-management-report__header">
      <div class="sync-management-report__icon" :class="`sync-management-report__icon--${status.color}`">
        <i :class="status.iconClass"></i>
      </div>
      <div>
        <div class="sync-management-report__id">{{ report.id }}</div>
        <div>{{ status.dateString }}</div>
      </div>
      <div class="sync-management-report__status">
        <div>
          <span class="mr-2"> Status</span>
          <snappy-badge :background="status.color">{{ status.name }}</snappy-badge>
        </div>
        <div class="sync-management-report__error">
          {{ normalizedError || '&nbsp;' }}
        </div>
      </div>
    </div>
    <div class="sync-management-report__cards">
      <div
        v-for="item in filteredReportData"
        :key="item.id"
        @click="toggleCard(item.id)"
        class="sync-management-report__card"
        :class="{'sync-management-report__card--active': activeCard === item.id}"
      >
        <div class="sync-management-report__number">{{ item.amount }}</div>
        <div>{{ item.name }}</div>
      </div>
    </div>

    <div v-if="activeCard">
      <div class="sync-management-report__contactsheader">
        <i class="fas fa-users"></i>
        <span class="sync-management-report__contactstitle">{{ activeCardObject.name }}</span>
        <button @click="activeCard = null" class="sync-management-report__close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="sync-management-report__table">
        <sync-management-report-table
          :label="activeCardObject.label"
          :syncId="report.id"
          :columns="['email', 'first_name', 'last_name', 'updated_at']"
          :headings="['Email', 'First Name', 'Last name', 'Date']"
          :sortable="['updated_at']"
        >
        <template v-if="hasTroubleshootingButton" #afterFilter>
          <button class="brn btn-primary" @click="$emit('clicked')">Troubleshoot These Contacts</button>
        </template>
        </sync-management-report-table>
      </div>
    </div>
  </div>
</template>

<script>
import SnappyBadge from '../SnappyBadge.vue';
import SyncManagementReportTable from './SyncManagementReportTable.vue';
import dateFormatter from '../../mixins/dateFormatter';
import {accountTroubleshootingReasons} from '../../components/account/troubleshooting/AccountTroubleshootingReasons';
import SnappyTable from "../SnappyTable.vue";


const cardTitles = {
  contactsCreated: 'Contacts created',
  contactsRejected: 'Contacts rejected',
  contactsUpdated: 'Contacts updated',
  contactsSynced: 'Contacts synced',
  contactsImported: 'Contacts imported',
}

export default {
  name: 'sync-management-report',
  mixins: [dateFormatter],
  components: {SnappyTable, SnappyBadge, SyncManagementReportTable},
  props: {
    report: {
      type: Object,
      required: true
    },
    timezone: {
      type: String,
    },
    troubleshooting: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      activeCard: null,
    };
  },
  methods: {
    toggleCard(id) {
      this.activeCard = this.activeCard ? null : id;
    }
  },
  computed: {
    normalizedError() {
      if(!this.report.error) return null;
      if(Object.keys(accountTroubleshootingReasons).includes(this.report.error)) {
        return this.report.error;
      } else {
        return 'Unknown Error';
      }
    },
    reportData() {
      return [
        {
          id: 1,
          name: cardTitles.contactsCreated,
          label: 'created',
          amount: this.report.created,
        },
        {
          id: 2,
          name: cardTitles.contactsRejected,
          label: 'rejected',
          amount: this.report.rejected,
        },
        {
          id: 3,
          name: cardTitles.contactsUpdated,
          label: 'updated',
          amount: this.report.updated,
        },
        {
          id: 4,
          name: Boolean(this.report.is_import) ? cardTitles.contactsImported : cardTitles.contactsSynced,
          label: 'synced',
          amount: this.report.synced,
        },
      ];
    },
    filteredReportData() {
      return this.reportData.filter(item => item.amount !== undefined);
    },
    activeCardObject() {
      return this.reportData.find(item => item.id === this.activeCard);
    },
    hasTroubleshootingButton() {
      return (this.activeCardObject?.name === cardTitles.contactsRejected) && this.report.troubleshooting?.length && this.report.success
    },
    crmName() {
      return this.report?.crm_name;
    },
    reportName() {
      if(!this.crmName) return '';
      const name = this.crmName.charAt(0).toUpperCase() + this.crmName.slice(1)
      return `${name} ${Boolean(this.report.is_import) ? ' Import' : ' Sync'}`;
    },
    status() {
      if (this.report.success) {
        return {
          name: 'Completed',
          color: 'green',
          dateString: `${this.reportName} Completed on ${this.dateLarge(new Date(this.report.finished_at), this.timezone)}`,
          iconClass: 'fas fa-check',
        };
      } else {
        return {
          name: 'Incomplete',
          color: 'red',
          dateString: `${this.reportName} Attempted on ${this.dateLarge(new Date(this.report.created_at), this.timezone)}`,
          iconClass: 'fas fa-exclamation',
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../sass/web/_variables.scss';

.sync-management-report {
  background: $white-color;
  border: 1px solid $platinum-color;
  border-top: none;
  padding: 20px 0;
  &__header {
    margin-bottom: 20px;
    padding: 0 16px;
    @media (min-width: $breakpoint-sm) {
      display: grid;
      grid-template-columns: min-content max-content 1fr min-content;
      grid-template-rows: 1fr;
      gap: 20px;
      padding: 0 25px;
    }
    & > * {
      align-self: center;
    }
  }
  &__icon {
    width: 32px;
    height: 32px;
    color: $white-color;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    &--green {
      background: $half-baked-color;
    }
    &--red {
      background: $deep-carmine-pink-color;
    }
  }
  &__id,
  &__status,
  &__contactstitle {
    font-weight: bold;
    color: $mine-shaft-color;
  }
  &__status {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }
  &__error {
    color: $deep-carmine-pink-color;
    font-weight: normal;
    word-break: break-all;
  }
  &__cards {
    display: flex;
    gap: 16px;
    padding: 0 25px 20px;
  }
  &__card {
    flex: 1;
    background: #f1f1f1;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 124px;
    width: inherit;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    text-align: center;
    color: $mine-shaft-color;
    &:hover,
    &--active {
      color: $white-color;
      background: $half-baked-color;
    }
  }
  &__number {
    font-size: 48px;
    line-height: 48px;
    font-weight: 700;
  }
  &__contactsheader {
    display: grid;
    grid-template-columns: min-content 1fr min-content;
    grid-template-rows: 1fr;
    color: $mine-shaft-color;
    gap: 20px;
    padding: 10px 25px;
    border-top: 1px solid $platinum-color;
    & > * {
      align-self: center;
    }
  }
  &__close {
    padding: 0;
    background: transparent;
    color: currentColor;
  }
  &__nocontacts {
    text-align: center;
  }
  &__table {
    padding: 0 25px 50px;
  }
}
</style>
